import { useCallback, useEffect, useRef, useState } from 'react';
import { App } from '@capacitor/app';
import { isNativePlatform } from '../native';
import { linkedinAPI } from '../api/client';
import {
  closeLinkedInBrowser,
  openLinkedInInSystemBrowser,
  parseLinkedInCallback,
} from './linkedinNative';

const OAUTH_TIMEOUT_MS = 105_000;
const PENDING_STORAGE_KEY = 'linkedin_oauth_pending';

export type LinkedInStatus =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'unavailable'
  | 'cancelled'
  | 'timed_out'
  | 'error';

export interface LinkedInProfile {
  first_name: string;
  last_name: string;
  email: string;
  email_verified: boolean;
  picture: string;
}

interface PendingOAuth {
  state: string;
  expiresAt: number;
}

const statusMessages: Record<LinkedInStatus, string> = {
  idle: 'Optional: pre-fill your name and photo from LinkedIn. You can edit everything after.',
  connecting: 'Finish connecting in LinkedIn, then return here.',
  connected: 'Pulled what LinkedIn shares (name and photo). Review the details below.',
  unavailable: 'LinkedIn connect is unavailable right now. Please fill in your details below.',
  cancelled: 'LinkedIn connection was cancelled. Select Connect to try again.',
  timed_out: 'LinkedIn did not respond in time. Select Connect to try again.',
  error: 'LinkedIn could not be connected. Select Connect to try again.',
};

function readPending(): PendingOAuth | null {
  try {
    const value = localStorage.getItem(PENDING_STORAGE_KEY);
    if (!value) return null;
    const pending = JSON.parse(value) as PendingOAuth;
    if (!pending.state || !pending.expiresAt) return null;
    return pending;
  } catch {
    return null;
  }
}

export function useLinkedInConnect() {
  const [status, setStatus] = useState<LinkedInStatus>('idle');
  const [profile, setProfile] = useState<LinkedInProfile | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const mountedRef = useRef(true);
  const pendingRef = useRef<PendingOAuth | null>(null);
  const timeoutRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const popupPollRef = useRef<ReturnType<typeof window.setInterval> | null>(null);
  const popupRef = useRef<Window | null>(null);
  const webMessageHandlerRef = useRef<((event: MessageEvent) => void) | null>(null);
  const callbackSeenRef = useRef(false);
  const fetchingResultRef = useRef(false);

  const clearTimersAndPopup = useCallback(() => {
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    if (popupPollRef.current) window.clearInterval(popupPollRef.current);
    timeoutRef.current = null;
    popupPollRef.current = null;
    if (webMessageHandlerRef.current) {
      window.removeEventListener('message', webMessageHandlerRef.current);
      webMessageHandlerRef.current = null;
    }
    popupRef.current?.close();
    popupRef.current = null;
  }, []);

  const settle = useCallback((
    nextStatus: LinkedInStatus,
    nextError = '',
    nextProfile: LinkedInProfile | null = null,
  ) => {
    clearTimersAndPopup();
    const pending = pendingRef.current;
    if (pending && readPending()?.state === pending.state) {
      localStorage.removeItem(PENDING_STORAGE_KEY);
    }
    pendingRef.current = null;
    callbackSeenRef.current = false;
    fetchingResultRef.current = false;
    if (mountedRef.current) {
      setStatus(nextStatus);
      setErrorMessage(nextError);
      if (nextProfile) setProfile(nextProfile);
    }
  }, [clearTimersAndPopup]);

  const startTimeout = useCallback((pending: PendingOAuth) => {
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    const remaining = pending.expiresAt - Date.now();
    if (remaining <= 0) {
      settle('timed_out');
      return false;
    }
    timeoutRef.current = window.setTimeout(() => settle('timed_out'), remaining);
    return true;
  }, [settle]);

  const fetchNativeResult = useCallback(async (state: string) => {
    if (fetchingResultRef.current || pendingRef.current?.state !== state) return;
    fetchingResultRef.current = true;
    try {
      const result = await linkedinAPI.getResult(state);
      if (pendingRef.current?.state !== state) return;
      if (result.status === 'pending') return;
      if (result.status === 'success') {
        settle('connected', '', result.profile);
      } else if (result.status === 'error') {
        settle(
          result.error === 'cancelled' ? 'cancelled' : 'error',
          result.error === 'cancelled' ? '' : 'LinkedIn could not complete the connection.',
        );
      }
    } catch {
      // 410/409 after a code exchange is expected: the state is one-time.
      // A parallel app-resume poll must not overwrite a successful connect.
    } finally {
      fetchingResultRef.current = false;
    }
  }, [settle]);

  const handleNativeUrl = useCallback((rawUrl: string) => {
    const callback = parseLinkedInCallback(rawUrl);
    if (!callback) return;

    const pending = pendingRef.current || readPending();
    if (!pending || callback.state !== pending.state) return;
    if (callbackSeenRef.current) return;

    pendingRef.current = pending;
    callbackSeenRef.current = true;
    void closeLinkedInBrowser();
    if (callback.error?.startsWith('state_')) {
      settle('error', 'This LinkedIn request is stale or invalid. Please try again.');
      return;
    }
    if (callback.code) {
      void linkedinAPI.exchange(callback.code, pending.state)
        .then((profile) => {
          if (pendingRef.current?.state !== pending.state) return;
          settle('connected', '', profile);
        })
        .catch(() => {
          if (pendingRef.current?.state === pending.state) {
            settle('error', 'LinkedIn could not complete the connection.');
          }
        });
      return;
    }
    void fetchNativeResult(pending.state);
  }, [fetchNativeResult, settle]);

  const recoverNativeFlow = useCallback(async () => {
    if (!isNativePlatform) return;
    if (callbackSeenRef.current) return;
    const pending = pendingRef.current || readPending();
    if (!pending) return;
    pendingRef.current = pending;
    if (!startTimeout(pending)) return;
    if (mountedRef.current) setStatus('connecting');
    await fetchNativeResult(pending.state);
  }, [fetchNativeResult, startTimeout]);

  useEffect(() => {
    mountedRef.current = true;
    if (!isNativePlatform) {
      return () => {
        mountedRef.current = false;
        clearTimersAndPopup();
      };
    }

    let disposed = false;
    const handles: Array<{ remove: () => Promise<void> }> = [];
    void Promise.all([
      App.addListener('appUrlOpen', ({ url }) => handleNativeUrl(url)),
      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) void recoverNativeFlow();
      }),
    ]).then((registered) => {
      if (disposed) {
        registered.forEach((handle) => void handle.remove());
      } else {
        handles.push(...registered);
      }
    });

    void App.getLaunchUrl().then((launch) => {
      if (launch?.url) handleNativeUrl(launch.url);
      void recoverNativeFlow();
    });

    return () => {
      disposed = true;
      mountedRef.current = false;
      clearTimersAndPopup();
      handles.forEach((handle) => void handle.remove());
    };
  }, [clearTimersAndPopup, handleNativeUrl, recoverNativeFlow, settle]);

  const connect = useCallback(async () => {
    clearTimersAndPopup();
    setStatus('connecting');
    setErrorMessage('');
    callbackSeenRef.current = false;
    const popup = isNativePlatform
      ? null
      : window.open('', 'linkedin-oauth', 'width=600,height=720');
    if (!isNativePlatform) {
      if (!popup) {
        settle('error', 'Your browser blocked the LinkedIn window. Allow popups and try again.');
        return;
      }
      popupRef.current = popup;
    }
    try {
      const flow = isNativePlatform ? 'native' : 'web';
      const auth = await linkedinAPI.getAuthUrl(flow);
      if (!auth.configured || !auth.authorization_url || !auth.state) {
        settle('unavailable');
        return;
      }

      const pending = { state: auth.state, expiresAt: Date.now() + OAUTH_TIMEOUT_MS };
      pendingRef.current = pending;
      if (!startTimeout(pending)) return;

      if (isNativePlatform) {
        localStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify(pending));
        void openLinkedInInSystemBrowser(auth.authorization_url);
        return;
      }

      if (!popup || popup.closed) {
        settle('cancelled');
        return;
      }
      popup.location.assign(auth.authorization_url);

      const handler = async (event: MessageEvent) => {
        if (
          event.origin !== window.location.origin
          || event.source !== popup
          || !event.data
          || event.data.type !== 'linkedin-oauth'
          || event.data.state !== pending.state
        ) {
          return;
        }
        callbackSeenRef.current = true;
        if (event.data.error) {
          settle(
            event.data.error === 'user_cancelled_authorize' ? 'cancelled' : 'error',
          );
          return;
        }
        if (!event.data.code) {
          settle('error', 'LinkedIn returned an incomplete response. Please try again.');
          return;
        }
        try {
          const linkedInProfile = await linkedinAPI.exchange(event.data.code, pending.state);
          settle('connected', '', linkedInProfile);
        } catch {
          settle('error', 'LinkedIn could not complete the connection.');
        }
      };
      webMessageHandlerRef.current = handler;
      window.addEventListener('message', handler);
      popupPollRef.current = window.setInterval(() => {
        if (popup.closed && !callbackSeenRef.current) settle('cancelled');
      }, 500);
    } catch {
      settle('unavailable');
    }
  }, [clearTimersAndPopup, settle, startTimeout]);

  return {
    connect,
    status,
    profile,
    errorMessage,
    statusMessage: errorMessage || statusMessages[status],
  };
}
